// ...existing code...
function normalizeApiBase(value) {
  const raw = (value || "/api").trim().replace(/\/+$/, "");
  if (!raw || raw === "/") return "/api";
  if (raw.endsWith("/api")) return raw;
  if (/^https?:\/\//i.test(raw)) return `${raw}/api`;
  return raw;
}

function shouldUseSameOriginApi() {
  if (typeof window === "undefined") return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "www.konvict-artz.com" || host === "konvict-artz.com";
}

const BASE = shouldUseSameOriginApi()
  ? "/api"
  : normalizeApiBase(import.meta.env.VITE_API_URL || "https://konvict-artz-backend-production.up.railway.app");

function getToken() {
  return localStorage.getItem("dex_token");
}

function authHeaders(extra = {}) {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

async function download(path, filename) {
  const res = await fetch(`${BASE}${path}`, {
    headers: authHeaders(),
  });

  if (!res.ok) {
    let error = { status: res.status, error: "Download failed" };
    try {
      error = { ...error, ...(await res.json()) };
    } catch {}
    throw error;
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  // Auth
  register: (body) => request("/auth/register", { method: "POST", body: JSON.stringify(body) }),
  login: (body) => request("/auth/login", { method: "POST", body: JSON.stringify(body) }),
  me: () => request("/auth/me"),
  forgotPassword: (body) => request("/auth/forgot-password", { method: "POST", body: JSON.stringify(body) }),
  resetPassword: (body) => request("/auth/reset-password", { method: "POST", body: JSON.stringify(body) }),

  // Dex
  chat: (message) => request("/dex/chat", { method: "POST", body: JSON.stringify({ message }) }),
  getDexAccess: () => request("/dex/access"),
  getHistory: () => request("/dex/history"),
  saveAppointment: (body) => request("/dex/appointment", { method: "POST", body: JSON.stringify(body) }),
  getAppointments: () => request("/dex/appointments"),
  deleteAppointment: (id) => request(`/dex/appointment/${id}`, { method: "DELETE" }),
  getSpecialDays: () => request("/dex/special-days"),
  createSpecialDay: (body) => request("/dex/special-days", { method: "POST", body: JSON.stringify(body) }),
  updateSpecialDay: (id, body) => request(`/dex/special-days/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteSpecialDay: (id) => request(`/dex/special-days/${id}`, { method: "DELETE" }),
  getLearningHistory: () => request("/dex/learning/history"),
  getDailyLesson: (body = {}) => request("/dex/learning/daily-lesson", { method: "POST", body: JSON.stringify(body) }),
  createLearningQuiz: (body = {}) => request("/dex/learning/quiz", { method: "POST", body: JSON.stringify(body) }),
  submitLearningQuiz: (body) => request("/dex/learning/quiz/submit", { method: "POST", body: JSON.stringify(body) }),
  getBriefing: () => request("/dex/briefing"),
  getFollowUps: () => request("/dex/follow-ups"),
  getTasks: () => request("/dex/tasks"),
  createTask: (body) => request("/dex/tasks", { method: "POST", body: JSON.stringify(body) }),
  updateTask: (id, body) => request(`/dex/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteTask: (id) => request(`/dex/tasks/${id}`, { method: "DELETE" }),
  getRelationshipAliases: () => request("/dex/relationship-aliases"),
  saveRelationshipAlias: (body) => request("/dex/relationship-aliases", { method: "POST", body: JSON.stringify(body) }),
  deleteRelationshipAlias: (id) => request(`/dex/relationship-aliases/${id}`, { method: "DELETE" }),
  getCommunications: () => request("/dex/communications"),
  createCommunicationDraft: (body) => request("/dex/communications", { method: "POST", body: JSON.stringify(body) }),
  updateCommunicationDraft: (id, body) => request(`/dex/communications/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  // Learned Preferences
  getPreferences: () => request("/dex/preferences"),
  setPreference: (key, value) => request("/dex/preferences", { method: "POST", body: JSON.stringify({ key, value }) }),
  getWorkflows: () => request("/dex/workflows"),
  saveWorkflow: (body) => request("/dex/workflows", { method: "POST", body: JSON.stringify(body) }),
  getDexShop: () => request("/dex/shop"),
  rewardDexCoins: (body) => request("/dex/shop/reward", { method: "POST", body: JSON.stringify(body) }),
  purchaseDexAccessory: (itemId) => request("/dex/shop/purchase", { method: "POST", body: JSON.stringify({ itemId }) }),
  saveDexColors: (colors) => request("/dex/shop/colors", { method: "POST", body: JSON.stringify({ colors }) }),

  // Call Events
  getCallEvents: () => request("/dex/call-events"),

  // Games
  dexChessMove: (body) => request("/dex/games/chess/move", { method: "POST", body: JSON.stringify(body) }),
  dexCheckersMove: (body) => request("/dex/games/checkers/move", { method: "POST", body: JSON.stringify(body) }),

  // TTS
  speakTts: (text) => fetch(`${BASE}/dex/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ text }),
  }),

  // Permissions
  getPermissions: () => request("/dex/permissions"),
  setPermissions: (permissions) => request("/dex/permissions", { method: "POST", body: JSON.stringify({ permissions }) }),
  getIntegrations: () => request("/dex/integrations"),
  saveVoiceRoute: (body) => request("/dex/integrations/voice", { method: "POST", body: JSON.stringify(body) }),

  // User Memory
  getMemory: () => request("/dex/memory"),
  setMemory: (key, value) => request("/dex/memory", { method: "POST", body: JSON.stringify({ key, value }) }),

  // Payments
  createCheckoutSession: () => request("/payments/checkout-session", { method: "POST", body: JSON.stringify({}) }),
  createCoinCheckout: (packId) => request("/payments/coins-checkout", { method: "POST", body: JSON.stringify({ packId }) }),
  getProducts: () => request("/payments/products"),
  createProductCheckout: (id, quantity = 1) => request(`/payments/products/${id}/checkout`, { method: "POST", body: JSON.stringify({ quantity }) }),
  subscribe: () => request("/payments/checkout-session", { method: "POST", body: JSON.stringify({}) }),
  openBillingPortal: () => request("/payments/portal", { method: "POST", body: JSON.stringify({}) }),
  getPaymentStatus: () => request("/payments/status"),

  // Bookings
  getServiceArea: (zip) => request(`/bookings/service-area${zip ? `?zip=${encodeURIComponent(zip)}` : ""}`),
  createBooking: (body) => request("/bookings", { method: "POST", body: JSON.stringify(body) }),

  // Affiliate
  getAffiliateDashboard: () => request("/affiliate/dashboard"),
  requestAffiliateCashout: (body) => request("/affiliate/cashout", { method: "POST", body: JSON.stringify(body) }),
  downloadAffiliateAndroid: () => download("/affiliate/android/download", "Dex-Assistant.apk"),

  // Admin
  getDiagnostics: () => request("/diagnostics/providers"),
  getAdminStats: () => request("/admin/stats"),
  getInventory: () => request("/admin/inventory"),
  addInventory: (body) => request("/admin/inventory", { method: "POST", body: JSON.stringify(body) }),
  updateInventory: (id, body) => request(`/admin/inventory/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteInventory: (id) => request(`/admin/inventory/${id}`, { method: "DELETE" }),
  getAffiliates: () => request("/admin/affiliates"),
  createAffiliate: (body) => request("/admin/affiliates/create", { method: "POST", body: JSON.stringify(body) }),
  getAffiliateInvites: () => request("/admin/affiliate-invites"),
  createAffiliateInvite: (body) => request("/admin/affiliate-invites/create", { method: "POST", body: JSON.stringify(body) }),
  resendAffiliateInvite: (id) => request(`/admin/affiliate-invites/${id}/resend`, { method: "POST", body: JSON.stringify({}) }),
  updateUserAccess: (id, body) => request(`/admin/users/${id}/access`, { method: "PATCH", body: JSON.stringify(body) }),
  sendPromo: (body) => request("/admin/send-promo", { method: "POST", body: JSON.stringify(body) }),
  sendTestEmail: (body) => request("/admin/email/test", { method: "POST", body: JSON.stringify(body) }),
  getUsers: () => request("/admin/users"),
  getBookings: () => request("/bookings/admin"),
  updateBooking: (id, body) => request(`/bookings/admin/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  getIntegrationRoutes: () => request("/admin/integrations/routes"),
  assignVoiceRoute: (body) => request("/admin/integrations/voice/assign", { method: "POST", body: JSON.stringify(body) }),
  getFeatureFlags: () => request("/admin/feature-flags"),
  updateFeatureFlag: (key, body) => request(`/admin/feature-flags/${key}`, { method: "PATCH", body: JSON.stringify(body) }),
};
